import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';

const isDev = import.meta.env.DEV;

// Simple logging function
function logPerf(name: string, value: number, rating: string) {
  if (!isDev) return;

  const status = rating === 'good' ? 'GOOD' : rating === 'needs-improvement' ? 'WARN' : 'POOR';
  console.log(`[PERF] ${name}: ${value.toFixed(1)}ms (${status})`);
}

// Setup Web Vitals tracking
function setupWebVitals() {
  onCLS(metric => logPerf('CLS', metric.value, metric.rating));
  onFCP(metric => logPerf('FCP', metric.value, metric.rating));
  onLCP(metric => logPerf('LCP', metric.value, metric.rating));
  onTTFB(metric => logPerf('TTFB', metric.value, metric.rating));
  onINP(metric => logPerf('INP', metric.value, metric.rating));
}

// Track API calls by wrapping fetch
function trackApiCalls() {
  if (typeof window === 'undefined') return;

  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    const start = performance.now();
    const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url;

    try {
      const response = await originalFetch(...args);
      const duration = performance.now() - start;

      if (isDev) {
        const status = response.status < 400 ? 'OK' : 'ERR';
        console.log(`[API] ${status} ${url}: ${duration.toFixed(0)}ms`);
      }

      return response;
    } catch (error) {
      const duration = performance.now() - start;
      if (isDev) {
        console.log(`[API] FAIL ${url}: ${duration.toFixed(0)}ms`);
      }
      throw error;
    }
  };
}

// Track memory usage periodically
function trackMemory() {
  if (!isDev || !('memory' in performance)) return;

  setInterval(() => {
    const memory = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
    if (memory) {
      const usedMB = Math.round(memory.usedJSHeapSize / 1024 / 1024);

      if (usedMB > 100) {
        console.warn(`[MEM] ${usedMB}MB (high usage)`);
      }
    }
  }, 30000);
}

// Time a function execution
function timeIt<T>(label: string, fn: () => T): T {
  const start = performance.now();
  const result = fn();
  const duration = performance.now() - start;

  if (isDev && duration > 10) {
    console.log(`[TIME] ${label}: ${duration.toFixed(1)}ms`);
  }

  return result;
}

// Time an async function execution
async function timeAsync<T>(label: string, fn: () => Promise<T>): Promise<T> {
  const start = performance.now();
  try {
    const result = await fn();
    const duration = performance.now() - start;

    if (isDev && duration > 100) {
      console.log(`[ASYNC] ${label}: ${duration.toFixed(1)}ms`);
    }

    return result;
  } catch (error) {
    const duration = performance.now() - start;
    if (isDev) {
      console.log(`[ASYNC] ${label} FAILED: ${duration.toFixed(1)}ms`);
    }
    throw error;
  }
}

// Initialize all performance tracking
function initPerformanceTracking() {
  setupWebVitals();
  trackApiCalls();
  if (isDev) {
    trackMemory();
  }
}

// Auto-initialize when imported
initPerformanceTracking();

// Export utilities
export { timeIt, timeAsync, logPerf };
